// EvidenceChips — compact "Evidence · N sources · ChipA · ChipB" strip used by
// the Executive Market Read, Top Signal cards, and the Signal Detail panel.
//
// Renders nothing when there's no evidence to show. Expands to a small list of
// source cards (domain · title · date · external link) on click. Raw URLs are
// never inlined into main dashboard text — they only appear inside the
// expanded source cards, as a tooltip/title on the link.

import React, { useState } from "react";
import { pickExampleChips } from "../../lib/signals/evidence";

const _hasText = (v) => typeof v === "string" && v.trim().length > 0;

const SourceCard = ({ src, index }) => {
    if (!src || !_hasText(src.url)) return null;
    const domain = src.domain || src.url.replace(/^https?:\/\//, "").split("/")[0];
    const showTitle = _hasText(src.title);
    return (
        <li className="flex gap-2 text-[11px]" data-testid={`evidence-source-${index}`}>
            <span className="text-[hsl(var(--mute))] num w-5 flex-none">{String(index + 1).padStart(2, "0")}</span>
            <div className="flex-1 min-w-0">
                {showTitle ? (
                    <a
                        href={src.url}
                        target="_blank"
                        rel="noreferrer"
                        title={src.url}
                        className="link-muted font-medium text-[hsl(var(--ink))] leading-snug block truncate"
                    >
                        {src.title}
                    </a>
                ) : (
                    <a
                        href={src.url}
                        target="_blank"
                        rel="noreferrer"
                        title={src.url}
                        className="link-muted font-medium text-[hsl(var(--ink))] leading-snug block truncate"
                    >
                        {domain}
                    </a>
                )}
                <div className="text-[hsl(var(--mute))] mt-0.5 flex flex-wrap gap-x-2 gap-y-0">
                    <span className="truncate">{domain}</span>
                    {_hasText(src.date) && <span>{src.date}</span>}
                </div>
            </div>
        </li>
    );
};

/**
 * Props:
 *  - evidence:    normalised evidence bundle (from normaliseEvidence /
 *                 readTopSignalEvidence / readLatestWeeklyEvidence)
 *  - sources:     optional explicit source-card list (defaults to
 *                 evidence.sourceLinks if not provided)
 *  - sourceCount: optional explicit source count badge (defaults to
 *                 evidence.sourceCount)
 *  - maxChips:    max example chips in compact view (default 2)
 *  - testIdScope: testid prefix for analytics (default "evidence")
 *  - dense:       when true, applies smaller text + tighter spacing for
 *                 use inside dense lists (e.g. Top Signal cards)
 *
 * Renders nothing when there is no evidence to show. The "Evidence · N
 * sources" pill stays visible even when 0 chips, as long as N > 0; the chips
 * stay visible even when source-count is 0.
 */
const EvidenceChips = ({ evidence, sources, sourceCount, maxChips = 2, testIdScope = "evidence", dense = false }) => {
    const [open, setOpen] = useState(false);
    if (!evidence || typeof evidence !== "object") return null;

    const cards = Array.isArray(sources) && sources.length > 0
        ? sources
        : (Array.isArray(evidence.sourceLinks) ? evidence.sourceLinks : []);
    const count = sourceCount != null ? sourceCount : (evidence.sourceCount || cards.length || 0);
    const chips = pickExampleChips(evidence, maxChips);

    if (count <= 0 && chips.length === 0) return null;

    const hasExpandable = cards.length > 0;
    // Pill base. `inline-flex items-center` + a fixed `min-h` give the pill a
    // stable height that does NOT depend on per-child font-size. `gap-1`
    // replaces per-child `mr-*`/`ml-*` margins so every child contributes
    // identical horizontal spacing. `whitespace-nowrap` prevents wrap-induced
    // baseline drift, `leading-none` collapses each child's line-box to its
    // em-box so the parent's `items-center` produces a single visual centre.
    const baseChip = "inline-flex items-center gap-1 px-2 rounded-full border border-[hsl(var(--line))] bg-[hsl(var(--surface-raised))] text-[hsl(var(--ink-soft))] whitespace-nowrap leading-none";
    // Dense pills (used inside takeaway/bullet rows) need a slightly tighter
    // height so they don't push line-spacing around. Non-dense pills match
    // the legacy 22px target.
    const sizing = dense ? "text-[10px] min-h-[20px]" : "text-[11px] min-h-[22px]";

    return (
        <div className="mt-2" data-testid={testIdScope}>
            <div className="flex flex-wrap items-center gap-1.5">
                <button
                    type="button"
                    onClick={() => hasExpandable && setOpen((v) => !v)}
                    disabled={!hasExpandable}
                    data-testid={`${testIdScope}-toggle`}
                    aria-expanded={open}
                    className={`${baseChip} ${sizing} font-medium ${hasExpandable ? "hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent-ink))] cursor-pointer" : "cursor-default"}`}
                    title={hasExpandable ? "Show sources" : "Source count"}
                >
                    {/* All three children inherit the parent's font-size and
                        `leading-none`. Each child is itself `inline-flex
                        items-center` so its glyph is centred inside its own
                        line-box, eliminating the EVIDENCE-vs-digits-vs-caret
                        baseline drift that `leading-none` alone could not fix. */}
                    <span className="inline-flex items-center uppercase tracking-wider text-[hsl(var(--mute))] font-semibold">Evidence</span>
                    {count > 0 && <span className="inline-flex items-center num">{count} source{count === 1 ? "" : "s"}</span>}
                    {hasExpandable && (
                        <span aria-hidden className="inline-flex items-center text-[hsl(var(--mute))]">{open ? "▴" : "▾"}</span>
                    )}
                </button>
                {chips.map((c, i) => (
                    <span
                        key={`${c}-${i}`}
                        className={`${baseChip} ${sizing}`}
                        data-testid={`${testIdScope}-chip-${i}`}
                    >
                        {c}
                    </span>
                ))}
            </div>
            {open && hasExpandable && (
                <ul className="mt-2 space-y-1.5 card-sunken p-2.5 rounded-md" data-testid={`${testIdScope}-sources`}>
                    {cards.map((src, i) => <SourceCard key={`${src.url}-${i}`} src={src} index={i} />)}
                </ul>
            )}
        </div>
    );
};

export default EvidenceChips;
